import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import { makeAerialEditCallbacks } from "fond/draw/modes/planner/aerial";
import { makeUndergroundEditCallbacks } from "fond/draw/modes/planner/underground";
import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import { validateMuPowerAddress, validateMuPowerLeadIn, validateMuPowerUgPath, validateMuPowerUgPoint } from "fond/draw/validation";
import {
  DeleteButton,
  JoinButton,
  LineStringButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  SplitButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { MuPowerEditableLayerIds, MuPowerInputLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />, <LineStringButton key="drawLineString" />],
  },
  {
    id: "modifyTools",
    type: "action",
    items: [<SplitButton key="split" />, <JoinButton key="join" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const aerial = { pointLayerId: MuPowerInputLayerIds.poles };

const undergroundCallbacks = makeUndergroundEditCallbacks({
  underground: { lineLayerId: MuPowerEditableLayerIds.ugPath, pointLayerId: MuPowerEditableLayerIds.ugPoints },
  aerial,
});

const aerialCallbacks = makeAerialEditCallbacks(aerial);

const MuPowerDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();

  const options: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      topologicalEditing: true,
      editOptions: {
        allLayers: [
          {
            ID: MuPowerInputLayerIds.addresses,
            GeometryType: "Point",
            validate: validateMuPowerAddress,
            getFeatureProperties: () => ({ layerId: MuPowerInputLayerIds.addresses }),
          },
          {
            ID: MuPowerEditableLayerIds.ugPoints,
            GeometryType: "Point",
            validate: validateMuPowerUgPoint,
            ...undergroundCallbacks.point,
          },
          {
            ID: MuPowerEditableLayerIds.ugPath,
            GeometryType: "LineString",
            validate: validateMuPowerUgPath,
            ...undergroundCallbacks.path,
          },
          {
            ID: MuPowerEditableLayerIds.leadins,
            GeometryType: "LineString",
            validate: validateMuPowerLeadIn,
            disabledTools: ["split", "join"],
            getFeatureProperties: () => ({ layerId: MuPowerEditableLayerIds.leadins }),
            options: { midPoints: false, maxVertices: 2 },
          },
          {
            ID: MuPowerInputLayerIds.poles,
            GeometryType: "Point",
            ...aerialCallbacks.point,
          },
        ],
        editingLayerId: MuPowerInputLayerIds.addresses,
        editingLayerIds: [
          MuPowerInputLayerIds.addresses,
          MuPowerEditableLayerIds.ugPoints,
          MuPowerEditableLayerIds.ugPath,
          MuPowerEditableLayerIds.leadins,
          MuPowerInputLayerIds.poles,
        ],
      },
    }),
    []
  );

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent title="Edit Base Data" alertTitle="Edit base data layers" onFinish={clear} />
    </MultiLayerEditProvider>
  );
};

export default MuPowerDraw;
